import React from "react";
import { CATEGORIES } from "../../data/diagnosticQuestions";
import styles from "./DiagnosticRing.module.scss";

const SIZE = 280;
const CENTER = SIZE / 2;
const OUTER_RADIUS = 120;
const INNER_RADIUS = 60;
const GAP_ANGLE = 3;
const SEGMENT_COUNT = 8;
const SEGMENT_ANGLE = (360 - SEGMENT_COUNT * GAP_ANGLE) / SEGMENT_COUNT;
const START_OFFSET = -90;

function polarToCartesian(cx, cy, radius, angleDeg) {
    const rad = (angleDeg * Math.PI) / 180;
    return {
        x: cx + radius * Math.cos(rad),
        y: cy + radius * Math.sin(rad),
    };
}

function describeArc(cx, cy, outerR, innerR, startAngle, endAngle) {
    const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
    const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
    const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
    const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return [
        `M ${outerStart.x} ${outerStart.y}`,
        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
        `L ${innerStart.x} ${innerStart.y}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
        "Z",
    ].join(" ");
}

export default function DiagnosticRing({ activeStep, completedSteps = [] }) {
    return (
        <div className={styles.ringContainer}>
            <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                width={SIZE}
                height={SIZE}
                className={styles.ringSvg}
            >
                {CATEGORIES.map((cat, i) => {
                    const startAngle =
                        START_OFFSET + i * (SEGMENT_ANGLE + GAP_ANGLE);
                    const endAngle = startAngle + SEGMENT_ANGLE;
                    const isActive = i === activeStep;
                    const isCompleted = completedSteps.includes(i);
                    const isInactive = !isActive && !isCompleted;

                    const path = describeArc(
                        CENTER,
                        CENTER,
                        OUTER_RADIUS,
                        INNER_RADIUS,
                        startAngle,
                        endAngle,
                    );

                    let fill = "#e2e8f0";
                    let opacity = 0.6;
                    let strokeColor = "none";
                    let strokeWidth = 0;

                    if (isCompleted) {
                        fill = cat.colorPrimary;
                        opacity = 1;
                    } else if (isActive) {
                        fill = `${cat.colorPrimary}15`;
                        opacity = 1;
                        strokeColor = cat.colorPrimary;
                        strokeWidth = 2.5;
                    }

                    return (
                        <g key={cat.id}>
                            <path d={path} fill={fill} opacity={opacity} />
                            {(isActive || isCompleted) && strokeWidth > 0 && (
                                <path
                                    d={path}
                                    fill="none"
                                    stroke={strokeColor}
                                    strokeWidth={strokeWidth}
                                    className={
                                        isActive ? styles.activeBorder : ""
                                    }
                                />
                            )}
                        </g>
                    );
                })}

                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={INNER_RADIUS - 8}
                    fill="#fff"
                />

                <text
                    x={CENTER}
                    y={CENTER - 6}
                    textAnchor="middle"
                    className={styles.centerStep}
                >
                    {activeStep + 1}/8
                </text>
                <text
                    x={CENTER}
                    y={CENTER + 14}
                    textAnchor="middle"
                    className={styles.centerLabel}
                >
                    {CATEGORIES[activeStep]?.[`name_is`]?.split(" ")[0] || ""}
                </text>
            </svg>
        </div>
    );
}
